import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaBell,
  FaCookieBite,
  FaShieldAlt,
  FaTimes,
  FaUserCircle,
} from "react-icons/fa";
import {
  cancelarPushSubscription,
  registrarPushSubscription,
} from "../../api/public";
import {
  PRIVACY_CONSENT_CHANGE_EVENT,
  getStoredPrivacyPreferences,
  hasCookieConsentDecision,
  savePrivacyPreferences,
} from "../../utils/privacyConsent";

const CATALOG_NUDGE_VISIBILITY_EVENT = "vintree:catalog-nudge-visibility";
const PUSH_PROMPT_DELAY_MS = 6000;
const PUSH_PROMPT_TRIGGER_SCROLLS = 12;
const SCROLL_STEP_PX = 300;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = `${base64String}${padding}`.replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

function getDeviceTipo() {
  if (typeof navigator === "undefined") return null;
  return /Mobi|Android/i.test(navigator.userAgent) ? "mobile" : "desktop";
}

function getNavegador() {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent;

  if (ua.includes("Edg/")) return "edge";
  if (ua.includes("Chrome/")) return "chrome";
  if (ua.includes("Firefox/")) return "firefox";
  if (ua.includes("Safari/")) return "safari";
  return "outro";
}

function PreferenceRow({ title, description, active, onToggle }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-gray-200 bg-white p-4">
      <div>
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        <p className="mt-1 text-xs leading-5 text-gray-600">{description}</p>
      </div>
      <button
        type="button"
        onClick={onToggle}
        className={`relative h-6 w-11 flex-shrink-0 rounded-full transition-colors ${
          active ? "bg-gray-900" : "bg-gray-300"
        }`}
        aria-pressed={active}
      >
        <span
          className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition-transform ${
            active ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </button>
    </div>
  );
}

function CatalogoFloatingControls({ loja, username }) {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [pushPromptVisible, setPushPromptVisible] = useState(false);
  const [pushPromptEligible, setPushPromptEligible] = useState(false);
  const [pushPromptDismissed, setPushPromptDismissed] = useState(false);
  const [catalogNudgeVisible, setCatalogNudgeVisible] = useState(false);
  const [cookieDecisionMade, setCookieDecisionMade] = useState(
    hasCookieConsentDecision()
  );
  const initialPrivacyPreferences = getStoredPrivacyPreferences();
  const [analyticsEnabled, setAnalyticsEnabled] = useState(
    initialPrivacyPreferences.analytics
  );
  const [marketingEnabled, setMarketingEnabled] = useState(
    initialPrivacyPreferences.marketing
  );
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushStateChecked, setPushStateChecked] = useState(false);
  const [notificationStatus, setNotificationStatus] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const [pushFeedback, setPushFeedback] = useState("");

  const nomeLoja = loja?.nome_fantasia || "esta loja";
  const pushDisponivel = Boolean(loja?.pushNotificationsEnabled);

  useEffect(() => {
    if (!loja) return undefined;

    if (hasCookieConsentDecision()) {
      setCookieDecisionMade(true);
      const preferences = getStoredPrivacyPreferences();
      setAnalyticsEnabled(preferences.analytics);
      setMarketingEnabled(preferences.marketing);
      return undefined;
    }

    const interval = window.setInterval(() => {
      if (hasCookieConsentDecision()) {
        setCookieDecisionMade(true);
        const preferences = getStoredPrivacyPreferences();
        setAnalyticsEnabled(preferences.analytics);
        setMarketingEnabled(preferences.marketing);
        window.clearInterval(interval);
      }
    }, 500);

    return () => window.clearInterval(interval);
  }, [loja]);

  useEffect(() => {
    const onPrivacyChange = (event) => {
      const preferences = event.detail || getStoredPrivacyPreferences();
      setCookieDecisionMade(true);
      setAnalyticsEnabled(Boolean(preferences.analytics));
      setMarketingEnabled(Boolean(preferences.marketing));
    };

    window.addEventListener(PRIVACY_CONSENT_CHANGE_EVENT, onPrivacyChange);

    return () => {
      window.removeEventListener(PRIVACY_CONSENT_CHANGE_EVENT, onPrivacyChange);
    };
  }, []);

  useEffect(() => {
    if (!loja) return undefined;

    const timer = window.setTimeout(() => {
      setPushPromptEligible(true);
    }, PUSH_PROMPT_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [loja]);

  useEffect(() => {
    if (!loja) return undefined;

    let scrollSteps = 0;
    let lastScrollY = window.scrollY;

    const onScroll = () => {
      const currentY = window.scrollY;
      const delta = currentY - lastScrollY;

      if (delta >= SCROLL_STEP_PX) {
        scrollSteps += Math.floor(delta / SCROLL_STEP_PX);
        lastScrollY = currentY;

        if (scrollSteps >= PUSH_PROMPT_TRIGGER_SCROLLS) {
          setPushPromptEligible(true);
        }
      } else if (delta < 0) {
        lastScrollY = currentY;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [loja]);

  useEffect(() => {
    if (!loja || !username || !pushDisponivel) {
      setPushStateChecked(true);
      return undefined;
    }
    if (
      typeof Notification === "undefined" ||
      !("serviceWorker" in navigator) ||
      !("PushManager" in window) ||
      Notification.permission !== "granted"
    ) {
      setPushEnabled(false);
      setPushStateChecked(true);
      return undefined;
    }

    let cancelled = false;

    async function syncExistingSubscription() {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        const subscription = await registration?.pushManager.getSubscription();

        if (cancelled) return;

        if (subscription) {
          await registrarPushSubscription(username, {
            ...subscription.toJSON(),
            deviceTipo: getDeviceTipo(),
            navegador: getNavegador(),
            idioma: navigator.language || null,
          });
          setPushEnabled(true);
          setPushPromptVisible(false);
          setPushPromptDismissed(true);
          setPushFeedback("Notificações ativadas para esta loja.");
        } else {
          setPushEnabled(false);
        }
        setPushStateChecked(true);
      } catch (error) {
        console.error("Erro ao verificar inscrição push:", error);
        setPushStateChecked(true);
      }
    }

    syncExistingSubscription();

    return () => {
      cancelled = true;
    };
  }, [loja, pushDisponivel, username]);

  useEffect(() => {
    const onCatalogNudgeVisibility = (event) => {
      setCatalogNudgeVisible(Boolean(event.detail?.visible));
    };

    window.addEventListener(
      CATALOG_NUDGE_VISIBILITY_EVENT,
      onCatalogNudgeVisibility
    );

    return () => {
      window.removeEventListener(
        CATALOG_NUDGE_VISIBILITY_EVENT,
        onCatalogNudgeVisibility
      );
    };
  }, []);

  useEffect(() => {
    if (
      !loja ||
      !pushDisponivel ||
      pushEnabled ||
      pushPromptDismissed ||
      privacyOpen ||
      catalogNudgeVisible ||
      !pushPromptEligible ||
      !cookieDecisionMade ||
      !pushStateChecked
    ) {
      setPushPromptVisible(false);
      return;
    }

    setPushPromptVisible(true);
  }, [
    catalogNudgeVisible,
    cookieDecisionMade,
    loja,
    privacyOpen,
    pushDisponivel,
    pushEnabled,
    pushPromptDismissed,
    pushPromptEligible,
    pushStateChecked,
  ]);

  const requestBrowserNotificationPermission = async () => {
    setPushFeedback("Ativando notificações...");

    if (!username) {
      setPushFeedback("Não foi possível identificar a loja desta vitrine.");
      return;
    }

    if (
      typeof Notification === "undefined" ||
      !("serviceWorker" in navigator) ||
      !("PushManager" in window)
    ) {
      setNotificationStatus("unsupported");
      setPushFeedback("Este navegador não suporta notificações push neste contexto.");
      return;
    }

    if (!VAPID_PUBLIC_KEY) {
      setPushFeedback("Configure REACT_APP_VAPID_PUBLIC_KEY no frontend.");
      return;
    }

    try {
      const permission =
        Notification.permission === "default"
          ? await Notification.requestPermission()
          : Notification.permission;
      setNotificationStatus(permission);

      if (permission !== "granted") {
        setPushEnabled(false);
        setPushFeedback(
          permission === "denied"
            ? "Notificações estão bloqueadas no navegador. Use o cadeado ao lado do endereço para permitir."
            : "Permissão de notificação não concedida."
        );
        return;
      }

      const registration = await navigator.serviceWorker.register(
        "/vintree-push-sw.js"
      );
      const existingSubscription = await registration.pushManager.getSubscription();
      const subscription =
        existingSubscription ||
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        }));

      await registrarPushSubscription(username, {
        ...subscription.toJSON(),
        deviceTipo: getDeviceTipo(),
        navegador: getNavegador(),
        idioma: navigator.language || null,
      });

      setPushEnabled(true);
      setPushFeedback("Notificações ativadas para esta loja.");
      setPushPromptVisible(false);
      setPushPromptDismissed(true);
    } catch (error) {
      console.error("Erro ao ativar notificacoes:", error);
      setNotificationStatus("default");
      setPushEnabled(false);
      setPushFeedback(
        error?.response?.data?.error ||
          error?.message ||
          "Não foi possível ativar notificações."
      );
    }
  };

  const unsubscribeBrowserPush = async () => {
    setPushFeedback("");

    try {
      if (!("serviceWorker" in navigator)) {
        setPushEnabled(false);
        return;
      }

      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();

      if (subscription) {
        await cancelarPushSubscription(username, {
          endpoint: subscription.endpoint,
        });
        await subscription.unsubscribe();
      }

      setPushEnabled(false);
      setPushFeedback("Notificações desativadas para esta loja.");
    } catch (error) {
      console.error("Erro ao desativar notificacoes:", error);
      setPushFeedback(
        error?.response?.data?.error ||
          error?.message ||
          "Não foi possível desativar notificações."
      );
    }
  };

  const togglePushPreference = () => {
    if (pushEnabled) {
      unsubscribeBrowserPush();
      return;
    }

    requestBrowserNotificationPermission();
  };

  const closePrivacyModal = () => {
    savePrivacyPreferences({
      analytics: analyticsEnabled,
      marketing: marketingEnabled,
    });
    setCookieDecisionMade(true);
    setPrivacyOpen(false);
  };

  return (
    <>
      <Link
        to="/entrar"
        className="fixed right-4 top-4 z-40 grid h-10 w-10 place-items-center rounded-full border border-gray-200 bg-white/90 text-slate-500 shadow-lg backdrop-blur transition hover:border-gray-300 hover:bg-white hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
        aria-label="Entrar no app"
        title="Entrar no app"
      >
        <FaUserCircle className="h-5 w-5" />
      </Link>

      <button
        type="button"
        onClick={() => setPrivacyOpen(true)}
        className="fixed bottom-4 left-4 z-40 grid h-10 w-10 place-items-center rounded-full border border-gray-200 bg-white/90 text-slate-500 shadow-lg backdrop-blur transition hover:border-gray-300 hover:bg-white hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
        style={{ bottom: "calc(1rem + env(safe-area-inset-bottom))" }}
        aria-label="Preferências de privacidade"
        title="Privacidade"
      >
        <FaShieldAlt className="h-4 w-4" />
      </button>

      {pushPromptVisible && !privacyOpen && (
        <div className="fixed bottom-16 left-1/2 z-40 w-[calc(100%-1.5rem)] max-w-md -translate-x-1/2 rounded-xl border border-gray-200 bg-white/95 p-4 text-gray-900 shadow-2xl backdrop-blur sm:bottom-6 sm:left-auto sm:right-4 sm:translate-x-0">
          <div className="flex items-start gap-3">
            <div className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-full bg-gray-100 text-gray-700">
              <FaBell className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-950">
                    Receber novidades?
                  </p>
                  <p className="mt-1 text-xs leading-5 text-gray-600">
                    {nomeLoja} pode avisar sobre promoções e produtos pelo
                    navegador.
                  </p>
                  {pushFeedback && (
                    <p className="mt-2 rounded-lg bg-slate-100 px-3 py-2 text-xs leading-5 text-slate-700">
                      {pushFeedback}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setPushPromptDismissed(true);
                    setPushPromptVisible(false);
                  }}
                  className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-800"
                  aria-label="Fechar convite de notificações"
                >
                  <FaTimes className="h-3.5 w-3.5" />
                </button>
              </div>

              <div className="mt-3 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setPushPromptDismissed(true);
                    setPushPromptVisible(false);
                  }}
                  className="rounded-full px-3 py-1.5 text-xs font-semibold text-gray-600 transition hover:bg-gray-100 hover:text-gray-900"
                >
                  Agora não
                </button>
                <button
                  type="button"
                  onClick={requestBrowserNotificationPermission}
                  className="rounded-full bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-gray-800"
                >
                  Receber
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {privacyOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/35 px-3 py-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="privacy-modal-title"
        >
          <div className="w-full max-w-lg overflow-hidden rounded-xl bg-gray-50 shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-200 bg-white px-5 py-4">
              <div>
                <h2
                  id="privacy-modal-title"
                  className="text-base font-semibold text-gray-950"
                >
                  Preferências de privacidade
                </h2>
                <p className="mt-1 text-xs leading-5 text-gray-600">
                  Ajustes de cookies e notificações do catálogo de{" "}
                  {nomeLoja}.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPrivacyOpen(false)}
                className="grid h-8 w-8 place-items-center rounded-full text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
                aria-label="Fechar preferências"
              >
                <FaTimes className="h-4 w-4" />
              </button>
            </div>

            <div className="max-h-[75vh] space-y-4 overflow-y-auto p-5">
              <section>
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <FaCookieBite className="h-4 w-4 text-gray-500" />
                  Cookies
                </div>
                <div className="space-y-3">
                  <PreferenceRow
                    title="Analytics da vitrine"
                    description="Ajuda a loja a entender visitas, buscas, cliques e interesse nos produtos."
                    active={analyticsEnabled}
                    onToggle={() => setAnalyticsEnabled((value) => !value)}
                  />
                  <PreferenceRow
                    title="Marketing e pixels"
                    description="Permite tags de marketing, pixels e medições de campanhas configuradas pela loja."
                    active={marketingEnabled}
                    onToggle={() => setMarketingEnabled((value) => !value)}
                  />
                </div>
              </section>

              <section>
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <FaBell className="h-4 w-4 text-gray-500" />
                  Notificações desta loja
                </div>
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {pushEnabled ? "Notificações ativadas" : "Notificações desativadas"}
                      </p>
                      <p className="mt-1 text-xs leading-5 text-gray-600">
                        {pushDisponivel
                          ? "Receba novidades, promoções e produtos pelo navegador. Esta autorização é separada dos cookies e pode ser cancelada aqui."
                          : "Esta loja não possui notificações disponíveis no plano atual."}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={togglePushPreference}
                      disabled={!pushDisponivel}
                      className={`relative h-6 w-11 flex-shrink-0 rounded-full transition-colors ${
                        pushEnabled ? "bg-gray-900" : "bg-gray-300"
                      }`}
                      aria-pressed={pushEnabled}
                    >
                      <span
                        className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                          pushEnabled ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                  <p className="mt-3 text-[11px] leading-4 text-gray-500">
                    {pushFeedback ||
                      (notificationStatus === "granted"
                        ? "Permissão concedida no navegador para esta loja."
                        : "Ativar notificações solicita a permissão nativa do navegador.")}
                  </p>
                </div>
              </section>
            </div>

            <div className="flex justify-end border-t border-gray-200 bg-white px-5 py-4">
              <button
                type="button"
                onClick={closePrivacyModal}
                className="rounded-full bg-gray-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-gray-800"
              >
                Concluir
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CatalogoFloatingControls;
